<script setup lang="ts">
import { APP_NAME } from "@/assets/js/appConfig";
import { marked } from "marked";

definePage({
	name: "PrivacyPolicyPage",
});

const CONTACT_EMAIL = "contact@olderone.com";

const privacyPolicyMarkdown = `
# Privacy Policy

**Effective Date:** July 2, 2026

Welcome to **${APP_NAME}** (“we”, “us”, “our”). This Privacy Policy explains how we collect, use, and protect information when you use our website.

## 1. Who We Are

${APP_NAME} is an informational and entertainment website focused on quick comparison games.

If you have any questions about this Privacy Policy, you can contact us at:

**Email:** ${CONTACT_EMAIL}

## 2. No Account Registration

Our website does **not** require account creation or login. We do not collect registration information such as usernames or passwords.

## 3. Information We Collect

When you use our website, we may collect limited information automatically, including:

- IP address
- browser type and device information
- referring pages
- basic technical logs required for security and site operation
- strictly necessary cookies or similar browser storage used to make the site function properly

We do **not** intentionally collect more personal data than is necessary to operate the website.

## 4. How We Use Information

We use the information we collect to:

- provide and operate the website
- maintain security and prevent abuse
- remember strictly necessary technical preferences
- troubleshoot errors and ensure proper performance
- comply with legal obligations

## 5. Legal Basis

Where applicable under data protection law, we process personal data on the basis of:

- our legitimate interests in operating, securing, and improving the website
- compliance with legal obligations

## 6. Cookies and Similar Technologies

We only use **strictly necessary cookies** and similar technologies required for core website functionality, security, and technical operation.

We do **not** use cookies for account login, advertising, or optional tracking unless this Privacy Policy and our Cookie Policy are updated.

For more information, please see our **Cookie Policy**.

## 7. Third-Party Content and Media Sources

Some content on this website is provided through the **TMDB API**.

**This product uses the TMDB API but is not endorsed or certified by TMDB.**

TMDB may have its own terms and policies that apply to content sourced from its services.

Cover art may be provided through MusicBrainz and the Cover Art Archive. The Cover Art Archive is a joint project of Internet Archive and MusicBrainz. Images remain copyrighted by their respective copyright owners.

## 8. Data Sharing

We do not sell your personal data.

We may share limited technical data with service providers only where necessary to host, secure, and operate the website, or where required by law.

## 9. Data Retention

We keep technical logs and similar operational data only for as long as reasonably necessary for security, maintenance, legal compliance, and troubleshooting.

Where TMDB content is cached by us, we aim to handle such caching in line with applicable TMDB terms.

## 10. Your Rights

Depending on your location, you may have rights regarding your personal data, including the right to request access, correction, deletion, restriction, or objection.

To exercise any applicable rights, contact us at **${CONTACT_EMAIL}**.

## 11. Children

This website is not intentionally directed at children under the age required by applicable law, and we do not knowingly collect personal data from children beyond ordinary technical data needed to operate the website.

## 12. Changes to This Policy

We may update this Privacy Policy from time to time. The updated version will be posted on this page with a revised effective date.

## 13. Contact

If you have questions about this Privacy Policy, contact:

**${CONTACT_EMAIL}**
`;

const privacyPolicyHtml = computed(() => marked.parse(privacyPolicyMarkdown));
</script>

<template>
	<section class="legal-page">
		<div class="overlay"></div>

		<RouterLink
			to="/"
			class="category-back-link"
		>
			← Back
		</RouterLink>

		<div class="legal-card">
			<div
				class="markdown-body"
				v-html="privacyPolicyHtml"
			></div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.legal-page {
	position: relative;
	min-height: 100dvh;
	padding: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	background: radial-gradient(
			circle at center,
			#2a2a2a 0%,
			#171717 46%,
			#070707 100%
		),
		#101010;
	// background-image: url("https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1600&q=80");
	// background-size: cover;
	// background-position: center;
	// background-repeat: no-repeat;
	// isolation: isolate;
}

.overlay {
	position: absolute;
	inset: 0;
	background: linear-gradient(
		180deg,
		rgba(255, 255, 255, 0.04) 0%,
		rgba(0, 0, 0, 0.08) 42%,
		rgba(0, 0, 0, 0.42) 100%
	);
	z-index: 0;
}
.legal-card {
	position: relative;
	z-index: 1;
	width: 100%;
	max-width: 920px;
	max-height: calc(100dvh - 40px);
	overflow-y: auto;
	overflow-x: hidden;
	padding: 28px 24px;
	border-radius: 24px;
	background: rgba(255, 255, 255, 0.1);
	border: 1px solid rgba(255, 255, 255, 0.14);
	backdrop-filter: blur(10px);
	box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
	scrollbar-width: thin;
	scrollbar-color: rgba(255, 255, 255, 0.18) transparent;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background: rgba(255, 255, 255, 0.16);
		border-radius: 999px;
		border: 2px solid transparent;
		background-clip: padding-box;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: rgba(255, 255, 255, 0.24);
		border: 2px solid transparent;
		background-clip: padding-box;
	}
}

.markdown-body {
	color: rgba(255, 255, 255, 0.9);
	line-height: 1.7;
	font-size: 15px;

	:deep(h1) {
		margin: 0 0 20px;
		font-size: clamp(32px, 5vw, 48px);
		line-height: 1;
		font-weight: 900;
		letter-spacing: -0.03em;
		color: $text-color-white;
	}

	:deep(h2) {
		margin: 28px 0 12px;
		font-size: 22px;
		font-weight: 800;
		color: $text-color-white;
	}

	:deep(p) {
		margin: 0 0 14px;
	}

	:deep(ul) {
		margin: 0 0 16px 18px;
		padding: 0;
	}

	:deep(li) {
		margin-bottom: 8px;
	}

	:deep(strong) {
		color: $text-color-white;
		font-weight: 800;
	}

	:deep(a) {
		color: $text-color-white;
	}
}

@media (max-width: 640px) {
	.legal-page {
		align-items: flex-start;
		padding: 76px 16px calc(24px + env(safe-area-inset-bottom));
		overflow-y: auto;
	}

	.legal-card {
		max-height: none;
		padding: 22px 18px;
		border-radius: 20px;
		overflow: visible;
	}

	.markdown-body {
		font-size: 14px;

		:deep(h2) {
			font-size: 20px;
		}
	}
}
</style>
